/**
 * WeatherWidget — compact "Adesso a Sirmione" teaser on the Home page.
 *
 * Uses the same /api/weather endpoint (Open-Meteo, no API key).
 * On any transient failure the widget silently returns null so the Home page
 * layout is never affected. Full weather details are one tap away on the
 * dedicated /categoria/meteo page (via the Info Utili grid).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Icons from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { weatherIconName, weatherLabel } from "@/lib/helpers";
import { fetchWeather } from "@/lib/weather";

export default function WeatherWidget() {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const lat = settings?.weather_lat ?? 45.4964;
    const lng = settings?.weather_lng ?? 10.6083;
    fetchWeather(lat, lng).then((res) => {
      if (!cancelled) setData(res);
    });
    return () => {
      cancelled = true;
    };
  }, [settings?.weather_lat, settings?.weather_lng]);

  const cur = data?.current;
  if (!cur || data?.error) return null;

  const IconCmp = Icons[weatherIconName(cur.weather_code)] || Icons.Cloud;

  return (
    <Link
      to="/categoria/meteo"
      data-testid="home-weather-teaser"
      className="mx-auto flex items-center justify-center gap-2.5 rounded-full bg-white/70 backdrop-blur border border-ink-200/40 shadow-soft px-4 py-2.5 hover:border-bronze-200 hover:shadow-elevated transition"
    >
      <IconCmp strokeWidth={1.3} className="w-5 h-5 text-bronze-400 shrink-0" />
      <span className="font-editorial italic text-lg text-ink-900 tabular-nums">
        {Math.round(cur.temperature_2m)}°
      </span>
      <span className="text-xs text-ink-500">
        {weatherLabel(cur.weather_code, i18n.language)}
      </span>
      <span className="text-[10px] font-semibold tracking-overline text-bronze-500 uppercase pl-1 border-l border-ink-200/50 ml-1 hidden xs:inline">
        {t("home.meteo_now")}
      </span>
    </Link>
  );
}
